/**
 * LockedFeaturePreview Component
 * Shows a blurred preview of premium features to entice free users to upgrade
 */

import React from 'react';
import { Lock, Sparkles, Zap, ArrowRight } from 'lucide-react';

const LockedFeaturePreview = ({ 
  featureName, 
  featureIcon,
  featureDescription,
  previewContent,
  unlockMethod = 'upgrade', // 'upgrade' or 'hire'
  agentName,
  onUpgrade,
  onHire,
  onGoToMarketplace
}) => {
  return (
    <div className="relative min-h-[600px] overflow-hidden" data-testid="locked-feature-preview">
      {/* Blurred Preview Background */}
      <div className="absolute inset-0 filter blur-sm opacity-40 pointer-events-none">
        {previewContent}
      </div>
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-900/80 via-gray-900/90 to-gray-900/95" />
      
      {/* Lock Content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-[600px] px-4">
        {/* Animated Lock Icon */}
        <div className="relative mb-6">
          <div className="absolute inset-0 bg-purple-500/20 rounded-full blur-xl animate-pulse" />
          <div className="relative w-24 h-24 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center shadow-2xl">
            <Lock className="w-12 h-12 text-white" />
          </div>
          <div className="absolute -top-1 -right-1 w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center animate-bounce">
            <Sparkles className="w-4 h-4 text-yellow-900" />
          </div>
        </div>

        {/* Feature Name */}
        <div className="flex items-center gap-3 mb-4">
          <span className="text-4xl">{featureIcon}</span>
          <h2 className="text-3xl font-bold text-white">{featureName}</h2>
        </div>

        {/* Description */}
        <p className="text-gray-300 text-center max-w-md mb-8 text-lg">
          {featureDescription}
        </p>

        {/* Feature Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 max-w-2xl">
          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 text-center">
            <div className="text-2xl mb-2">🎯</div>
            <div className="text-white font-medium">Smart Targeting</div>
            <div className="text-gray-400 text-sm">AI-powered precision</div>
          </div>
          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 text-center">
            <div className="text-2xl mb-2">⚡</div>
            <div className="text-white font-medium">10x Faster</div>
            <div className="text-gray-400 text-sm">Automate your workflow</div>
          </div>
          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 text-center">
            <div className="text-2xl mb-2">📈</div>
            <div className="text-white font-medium">Proven Results</div>
            <div className="text-gray-400 text-sm">Data-driven growth</div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          {unlockMethod === 'upgrade' ? (
            <button
              onClick={onUpgrade}
              className="group px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-xl font-bold text-lg shadow-lg shadow-purple-500/25 transition-all duration-300 flex items-center gap-2"
              data-testid="upgrade-to-pro-btn"
            >
              <Zap className="w-5 h-5" />
              Upgrade to Pro
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          ) : (
            <button
              onClick={onHire}
              className="group px-8 py-4 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white rounded-xl font-bold text-lg shadow-lg shadow-amber-500/25 transition-all duration-300 flex items-center gap-2"
              data-testid="hire-agent-btn"
            >
              <Sparkles className="w-5 h-5" />
              Hire {agentName}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          )}
          
          <button
            onClick={onGoToMarketplace}
            className="px-8 py-4 bg-gray-800 hover:bg-gray-700 text-white rounded-xl font-medium text-lg border border-gray-600 transition-all duration-300"
            data-testid="go-to-marketplace-btn"
          >
            Browse Marketplace
          </button>
        </div>

        {/* Trust Badge */}
        <div className="mt-8 flex items-center gap-2 text-gray-400 text-sm">
          <div className="flex -space-x-2">
            <div className="w-6 h-6 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full border-2 border-gray-900" />
            <div className="w-6 h-6 bg-gradient-to-br from-green-400 to-teal-500 rounded-full border-2 border-gray-900" />
            <div className="w-6 h-6 bg-gradient-to-br from-orange-400 to-red-500 rounded-full border-2 border-gray-900" />
          </div>
          <span>Join 10,000+ marketers using AI agents</span>
        </div>
      </div>
    </div>
  );
};

// Pre-built preview content for each agent room
export const AthenaPreviewContent = () => (
  <div className="p-8 bg-gray-900">
    <div className="grid grid-cols-3 gap-6">
      <div className="col-span-2 bg-gray-800 rounded-xl p-6 h-64">
        <div className="h-4 bg-gray-700 rounded w-1/3 mb-4" />
        <div className="space-y-2">
          <div className="h-3 bg-gray-700 rounded w-full" />
          <div className="h-3 bg-gray-700 rounded w-5/6" />
          <div className="h-3 bg-gray-700 rounded w-4/6" />
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4">
          <div className="h-20 bg-amber-500/20 rounded-lg" />
          <div className="h-20 bg-orange-500/20 rounded-lg" />
        </div>
      </div>
      <div className="space-y-4">
        <div className="bg-gray-800 rounded-xl p-4 h-28">
          <div className="h-3 bg-gray-700 rounded w-2/3 mb-2" />
          <div className="h-16 bg-amber-500/10 rounded-lg" />
        </div>
        <div className="bg-gray-800 rounded-xl p-4 h-28">
          <div className="h-3 bg-gray-700 rounded w-1/2 mb-2" />
          <div className="h-16 bg-orange-500/10 rounded-lg" />
        </div>
      </div>
    </div>
  </div>
);

export const HermesPreviewContent = () => (
  <div className="p-8 bg-gray-900">
    <div className="flex gap-6">
      <div className="w-1/3 space-y-4">
        {[1,2,3,4].map(i => (
          <div key={i} className="bg-gray-800 rounded-lg p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500/20 rounded-full" />
            <div className="flex-1">
              <div className="h-3 bg-gray-700 rounded w-2/3 mb-1" />
              <div className="h-2 bg-gray-700 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
      <div className="flex-1 bg-gray-800 rounded-xl p-6">
        <div className="h-4 bg-gray-700 rounded w-1/4 mb-6" />
        <div className="space-y-3">
          {[1,2,3,4,5].map(i => (
            <div key={i} className="h-3 bg-gray-700 rounded" style={{width: `${100 - i*10}%`}} />
          ))}
        </div>
        <div className="mt-8 flex gap-4">
          <div className="h-10 bg-orange-500/30 rounded-lg flex-1" />
          <div className="h-10 bg-red-500/30 rounded-lg flex-1" />
        </div>
      </div>
    </div>
  </div>
);

export const ApolloPreviewContent = () => (
  <div className="p-8 bg-gray-900">
    <div className="grid grid-cols-5 gap-4 mb-6">
      {['LinkedIn', 'Twitter', 'Facebook', 'Instagram', 'Medium'].map((platform, i) => (
        <div key={platform} className="bg-gray-800 rounded-lg p-4 text-center">
          <div className="w-12 h-12 bg-yellow-500/20 rounded-full mx-auto mb-2" />
          <div className="h-3 bg-gray-700 rounded w-2/3 mx-auto" />
        </div>
      ))}
    </div>
    <div className="bg-gray-800 rounded-xl p-6">
      <div className="flex justify-between items-center mb-4">
        <div className="h-4 bg-gray-700 rounded w-1/4" />
        <div className="h-8 bg-yellow-500/30 rounded-lg w-32" />
      </div>
      <div className="grid grid-cols-3 gap-4">
        {[1,2,3].map(i => (
          <div key={i} className="h-32 bg-gray-700/50 rounded-lg" />
        ))}
      </div>
    </div>
  </div>
);

export const PantheonPreviewContent = () => (
  <div className="p-8 bg-gray-900">
    <div className="text-center mb-8">
      <div className="h-6 bg-gray-700 rounded w-1/3 mx-auto mb-2" />
      <div className="h-3 bg-gray-700 rounded w-1/2 mx-auto" />
    </div>
    <div className="grid grid-cols-3 gap-6">
      {['Athena', 'Hermes', 'Apollo'].map((god, i) => (
        <div key={god} className="bg-gray-800 rounded-xl p-6 text-center">
          <div className="w-16 h-16 bg-purple-500/20 rounded-full mx-auto mb-4" />
          <div className="h-4 bg-gray-700 rounded w-2/3 mx-auto mb-2" />
          <div className="h-3 bg-gray-700 rounded w-1/2 mx-auto" />
          <div className="mt-4 h-10 bg-purple-500/30 rounded-lg" />
        </div>
      ))}
    </div>
    <div className="mt-6 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl p-6 text-center">
      <div className="h-4 bg-gray-700 rounded w-1/4 mx-auto mb-2" />
      <div className="h-12 bg-purple-500/30 rounded-lg w-48 mx-auto" />
    </div>
  </div>
);

export const DionysusPreviewContent = () => (
  <div className="p-8 bg-gray-900">
    <div className="grid grid-cols-3 gap-6">
      <div className="bg-gray-800 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 bg-purple-500/30 rounded-full" />
          <div className="h-4 bg-gray-700 rounded w-2/3" />
        </div>
        <div className="space-y-3">
          {[1,2,3].map(i => (
            <div key={i} className="bg-gray-700/50 rounded-lg p-3">
              <div className="h-3 bg-gray-600 rounded w-3/4 mb-1" />
              <div className="h-2 bg-gray-600 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
      <div className="bg-gray-800 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 bg-red-500/30 rounded-full" />
          <div className="h-4 bg-gray-700 rounded w-2/3" />
        </div>
        <div className="bg-gray-700/50 rounded-lg p-4 h-40">
          <div className="space-y-2">
            {[1,2,3,4].map(i => (
              <div key={i} className="h-2 bg-gray-600 rounded" style={{width: `${100 - i*15}%`}} />
            ))}
          </div>
        </div>
      </div>
      <div className="bg-gray-800 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 bg-orange-500/30 rounded-full" />
          <div className="h-4 bg-gray-700 rounded w-2/3" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          {[1,2,3,4].map(i => (
            <div key={i} className="bg-gray-700/50 rounded-lg p-3 text-center">
              <div className="h-6 bg-gray-600 rounded w-1/2 mx-auto mb-1" />
              <div className="h-2 bg-gray-600 rounded w-2/3 mx-auto" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default LockedFeaturePreview;
